import { useEffect, useRef, useState, useMemo } from "react"
import { useField } from "formik"
import { makeStyles } from "tss-react/mui"
import { faPen, faXmark } from "@fortawesome/pro-solid-svg-icons"
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome"
import { Box, IconButton, Popover, Stack, Typography } from "@mui/material"

import { LocalizedStrict } from "../../../../../../modules/localization/components/AppLocalized"
import { useDateTime } from "../../../../../../modules/localization/useDateTime"
import { StandardButton } from "../../../../buttons/StandardButton"
import { CreateOrderDialogMode, CreateOrderFlowType, MeetingServicePriceListState } from "../../../createOrderTypes"
import { useCreateOrderFormConfig, useCreateOrderFormState } from "../../../mainForm/CreateOrderFormContext"
import { useConfirmAbandonDialog } from "../../../hooks/useConfirmAbandonDialog"
import { useCreateOrderDialogType } from "../../../hooks/useCreateOrderDialogType"
import { useMeetingServicePriceListStatus } from "../../../hooks/useMeetingServicePriceListStatus"
import { defaultMultipleDeliveries } from "../../../defaults"
import { CreateOrderCloseConfirmDialog } from "../../CreateOrderCloseConfirmDialog"
import { DeliveryTimesField, MeetingServiceConfig, DynamicConfigKey } from "../../form/formTypes"
import { NullableCalendarEvent } from "../../resource/calendar/fullCalendarTypes"

import { CreateUpdateDeliveryTimePopover } from "./CreateUpdateDeliveryTimePopover"
import { AddDeliveryTimeButton } from "./components/AddDeliveryTimeButton"
import { DeliveryTimeDisplay } from "./components/DeliveryTimeDisplay"
import { DAY_OF_WEEK_NUMBER, MAX_DELIVERIES, sortedDeliveryTimes, validateDeliveryDate } from "./helpers"

const useStyles = makeStyles()((theme) => ({
    title: {
        color: theme.palette.tailwind.slate[500],
    },
    container: {
        display: "flex",
        alignItems: "center",
        background: theme.palette.tailwind.slate[100],
        padding: "0.5rem",
        borderRadius: "1.5rem",
        width: "fit-content",
        gap: "0.5rem",
        position: "relative",
    },
    iconButton: {
        padding: ".5rem",
        color: theme.palette.tailwind.slate[500],
        "& svg": {
            fontSize: "1.25rem",
        },
    },
    closeButton: {
        position: "absolute",
        top: ".5rem",
        right: ".5rem",
    },
    classNameOverride: {
        padding: "1.25rem 1.5rem 3.25rem",
    },
}))

type DeliveryTimeSelectorProps = Readonly<{
    meetingServiceConfiguration: MeetingServiceConfig
    handleCloseMainDialog: () => void
    allowOutsideBusinessHours: boolean
    canOverrideClosingDates: boolean
    allowPastDates: boolean
    disabled: boolean
}>

export function DeliveryTimeSelector({
    meetingServiceConfiguration,
    handleCloseMainDialog,
    allowOutsideBusinessHours,
    canOverrideClosingDates,
    allowPastDates,
    disabled,
}: DeliveryTimeSelectorProps) {
    const { classes } = useStyles()
    const dateTimeFactory = useDateTime()
    const { flowConfig } = useCreateOrderFormConfig()
    const { orderContext } = useCreateOrderFormState()
    const { isRegularOrder } = useCreateOrderDialogType()
    const canEditDeliveryDate = isRegularOrder && orderContext.mode === CreateOrderDialogMode.EDIT

    const [{ value: priceListState }] = useField<MeetingServicePriceListState>("priceList")
    const priceList: MeetingServicePriceListState["item"] = priceListState.item
    const { isReady } = useMeetingServicePriceListStatus(priceListState.status)
    const [{ value: selectedTimesValue }, , selectedTimesHelpers] = useField<DeliveryTimesField | undefined>(
        DynamicConfigKey.MULTIPLE_DELIVERIES,
    )
    const selectedTimes: DeliveryTimesField = selectedTimesValue || defaultMultipleDeliveries

    const [{ value: localEvent }] = useField<NullableCalendarEvent>("event")

    const isMultipleDeliveriesEnabled = useMemo(() => {
        return Boolean(
            meetingServiceConfiguration.sections
                .flatMap(({ groups }) => groups.flatMap(({ fields }) => fields))
                .find((field) => field.key === DynamicConfigKey.MULTIPLE_DELIVERIES)?.active,
        )
    }, [meetingServiceConfiguration.sections])

    const maxAllowedDeliveries = isMultipleDeliveriesEnabled ? MAX_DELIVERIES : 1

    const eventStartTime = useMemo(() => {
        return localEvent?.start ? dateTimeFactory.fromISO(localEvent.start) : null
    }, [localEvent?.start, dateTimeFactory])

    const hasTimes = !!selectedTimes?.deliveryTimes?.length
    const isDeliverySet = Boolean(selectedTimes?.deliveryDate) && hasTimes

    const { deliveryTimes, closingDates } = meetingServiceConfiguration

    // Closing dates always reach the picker: whoever may override them is warned, not hidden from.
    const effectiveClosingDates = useMemo(() => closingDates ?? [], [closingDates])
    const effectiveOpeningHours = useMemo(
        () => (allowOutsideBusinessHours ? [] : (deliveryTimes ?? [])),
        [allowOutsideBusinessHours, deliveryTimes],
    )

    const allowedWeekdays = useMemo(() => {
        return allowOutsideBusinessHours ? DAY_OF_WEEK_NUMBER : effectiveOpeningHours?.map((h) => h.dayOfWeek) || []
    }, [effectiveOpeningHours])

    // A date already on the order stays pickable even if the shop has since closed that day —
    // the user is editing something they did not break.
    const grandfatheredDate = useMemo(
        () =>
            canEditDeliveryDate && selectedTimes.deliveryDate
                ? dateTimeFactory.fromISO(selectedTimes.deliveryDate)
                : null,
        [canEditDeliveryDate, selectedTimes.deliveryDate, dateTimeFactory],
    )

    useEffect(() => {
        if (isMultipleDeliveriesEnabled) return
        if (!selectedTimesValue?.deliveryTimes || selectedTimesValue.deliveryTimes.length <= 1) return

        const [earliest] = sortedDeliveryTimes(selectedTimesValue.deliveryTimes)
        selectedTimesHelpers.setValue({
            ...selectedTimesValue,
            deliveryTimes: [earliest],
            activeTime: earliest?.time ?? null,
        })
    }, [isMultipleDeliveriesEnabled, selectedTimesHelpers, selectedTimesValue])

    const containerRef = useRef<HTMLDivElement>(null)
    const [popoverPosition, setPopoverPosition] = useState({ left: 0, top: 0 })
    const { confirmAbandonDialogState, handleOpenConfirmAbandonDialog, handleCloseConfirmAbandonDialog } =
        useConfirmAbandonDialog()
    const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null)
    const open = Boolean(anchorEl)

    useEffect(() => {
        if (!isReady) return

        const shouldAutoOpen = !isDeliverySet || (flowConfig?.flow === CreateOrderFlowType.OUTLOOK && !isDeliverySet)

        if (shouldAutoOpen && containerRef.current) {
            const rect = containerRef.current.getBoundingClientRect()
            setPopoverPosition({
                left: 40,
                top: rect.bottom,
            })
            setAnchorEl(containerRef.current)
        }
    }, [flowConfig, isDeliverySet, isReady])

    const handlePopoverOpen = (event: React.MouseEvent<HTMLElement>) => {
        if (!priceList) return
        const rect = event.currentTarget.getBoundingClientRect()
        setPopoverPosition({
            left: rect.left,
            top: rect.bottom,
        })
        setAnchorEl(event.currentTarget)
    }

    const handlePopoverClose = () => {
        // Only allow closing if there are selected times
        const isValidDate =
            isDeliverySet &&
            validateDeliveryDate(
                dateTimeFactory.fromISO(selectedTimes.deliveryDate!),
                {
                    allowedWeekdays,
                    closingDates: effectiveClosingDates,
                    canOverrideClosingDates,
                    grandfatheredDate,
                },
                dateTimeFactory,
            ).isSelectable

        if (!isValidDate) {
            handleOpenConfirmAbandonDialog()
            return
        }

        // The delivery time is independent of the booking time: a meeting starting outside
        // the shop's delivery window is fine as long as a valid delivery time was picked,
        // so it must not raise the abandon prompt.
        setAnchorEl(null)
    }

    const onSubmit = (deliveries: DeliveryTimesField) => {
        const sortedTimes = sortedDeliveryTimes(deliveries.deliveryTimes)
        const updatedDeliveries = {
            ...deliveries,
            deliveryTimes: sortedTimes,
            activeTime: sortedTimes[0]?.time || null,
        }
        selectedTimesHelpers.setValue(updatedDeliveries)
        // Close popover only if we have at least one delivery time
        if (updatedDeliveries.deliveryTimes.length > 0) {
            setAnchorEl(null)
        }
    }

    const handleConfirm = () => {
        handleCloseConfirmAbandonDialog()
    }
    return (
        <Stack gap=".5rem">
            {!disabled && (
                <Stack flexDirection="row" marginLeft="1rem">
                    {hasTimes && (
                        <LocalizedStrict id="create-order-delivery-times-update-delivery-time">
                            <StandardButton
                                size="small"
                                variant="text"
                                fullWidth={false}
                                onClick={handlePopoverOpen}
                                updateDefaultClasses={classes.iconButton}
                                endIcon={<FontAwesomeIcon icon={faPen} />}
                            >
                                Update delivery time
                            </StandardButton>
                        </LocalizedStrict>
                    )}
                </Stack>
            )}

            <Box className={classes.container} ref={containerRef}>
                {isDeliverySet ? (
                    <>
                        <DeliveryTimeDisplay maxAllowedDeliveries={maxAllowedDeliveries} disabled={disabled} />
                        {selectedTimes.deliveryTimes.length < maxAllowedDeliveries && !disabled && (
                            <AddDeliveryTimeButton openingHours={effectiveOpeningHours} />
                        )}
                    </>
                ) : (
                    <LocalizedStrict id="create-order-delivery-times-add-delivery-time">
                        <Typography variant="body2" className={classes.title}>
                            Add delivery time:
                        </Typography>
                    </LocalizedStrict>
                )}
            </Box>

            {open && (
                <Popover
                    open={open}
                    anchorEl={anchorEl}
                    onClose={handlePopoverClose}
                    anchorReference="anchorPosition"
                    anchorPosition={popoverPosition}
                    anchorOrigin={{
                        vertical: "bottom",
                        horizontal: "left",
                    }}
                    transformOrigin={{
                        vertical: "top",
                        horizontal: "left",
                    }}
                    disableRestoreFocus
                >
                    <Box>
                        <IconButton onClick={handlePopoverClose} className={classes.closeButton}>
                            <FontAwesomeIcon icon={faXmark} />
                        </IconButton>

                        <CreateUpdateDeliveryTimePopover
                            initialValues={selectedTimes}
                            eventStartTime={eventStartTime}
                            priceList={priceList!}
                            onSubmit={onSubmit}
                            allowedWeekdays={allowedWeekdays}
                            closingDates={effectiveClosingDates}
                            canOverrideClosingDates={canOverrideClosingDates}
                            grandfatheredDate={grandfatheredDate}
                            openingHours={effectiveOpeningHours}
                            maxAllowedDeliveries={maxAllowedDeliveries}
                            handlePopoverClose={handlePopoverClose}
                            allowPastDates={allowPastDates}
                        />
                    </Box>
                </Popover>
            )}
            {confirmAbandonDialogState && (
                <CreateOrderCloseConfirmDialog
                    open={confirmAbandonDialogState}
                    onClose={handleConfirm}
                    onDiscard={handleCloseMainDialog}
                />
            )}
        </Stack>
    )
}
